// src/components/Adviser/Tabs/LicenseCertificates/LicenseCertificateFields.js

export const COMPETENT_ADVISER_TEST_FIELDS = [
  {
    key: "moneyLaunderingRegulationsPdp",
    label: "Money Laundering Regulations PDP",
    type: "date",
  },
  {
    key: "gdprAwarenessPdp",
    label: "GDPR Awareness PDP",
    type: "date",
  },
  {
    key: "complianceReadingPdp",
    label: "Complaint Handling PDP",
    type: "date",
  },
  {
    key: "mortgageAndProtectionPdp",
    label: "Mortgage Products & Regulations PDP",
    type: "date",
  },
  {
    key: "protectionTest",
    label: "Protection Test",
    type: "date",
  },
  {
    key: "pensionSwitchAndDefinedContributionsPdp",
    label: "Pension Switch & Defined Contributions PDP",
    type: "date",
  },
  {
    key: "rriarTest",
    label: "Regulated Retail Investment Adviser Re-Evaluation (RRIAR) Test",
    type: "date",
  },
  {
    key: "vct",
    label: "VCT Test",
    type: "date",
  },
  {
    key: "longTermCareTest",
    label: "Long Term Care Test",
    type: "date",
  },
  {
    key: "equityReleaseTest",
    label: "Equity Release Test",
    type: "date",
  },
  {
    key: "consumerDutyTest",
    label: "Consumer Duty Test",
    type: "date",
  },
  //   {
  //     key: "notes",
  //     label: "Notes",
  //     type: "text",
  //   },
];

export const DEFAULT_COMPETENT_ADVISER_TEST_KEYS =
  COMPETENT_ADVISER_TEST_FIELDS.map((field) => field.key);

export const COMPETENT_ADVISER_TEST_OPTIONS = COMPETENT_ADVISER_TEST_FIELDS.map(
  (field) => ({
    id: field.key,
    name: field.label,
  }),
);

export const SPS_ACCREDITED_BODY_OPTIONS = [
  { id: "LIBF", name: "LIBF" },
  { id: "CII", name: "CII" },
  { id: "CISI", name: "CISI" },
  { id: "N/A", name: "N/A" },
];

export const canViewLicenseCertificateField = (
  field,
  userPermissions = [],
) => {
  if (!field?.restrictedToPermissions?.length) return true;

  const permissions = new Set(
    (Array.isArray(userPermissions) ? userPermissions : []).map((permission) =>
      String(permission || "").trim().toLowerCase(),
    ),
  );

  return field.restrictedToPermissions.some((permission) =>
    permissions.has(String(permission).toLowerCase()),
  );
};

export const CREDIT_FIELD_PERMISSIONS = [
  "Administrator",
  "Compliance",
  "View Credit Score",
];

export const ADVISER_RESPONDED_TOOLTIP =
  "Select Yes when the adviser has responded. Yes stops future reminder emails for this section. No or no selection keeps reminders enabled.";

export const canViewCreditHistory = (userPermissions = []) =>
  canViewLicenseCertificateField(
    { restrictedToPermissions: CREDIT_FIELD_PERMISSIONS },
    userPermissions,
  );

export const LICENSE_CERTIFICATE_SECTIONS = [
  {
    key: "dplCertificate",
    title: "DPL Certificate",
    fields: [
      { key: "dplName", label: "DPL Name" },
      {
        key: "dplRegistrationNumber",
        label: "DPL Registration Number",
      },
      {
        key: "dplExpiryDate",
        label: "DPL Expiry Date",
        type: "date",
      },
      {
        key: "notifyAdviser",
        label: "Notify adviser",
        inputType: "booleanRadio",
        defaultValue: true,
        tooltip:
          "Controls whether reminder emails are sent for this DPL certificate. Select No to stop DPL reminders.",
      },
      { key: "notes", label: "Notes", inputType: "textarea" },
      {
        key: "adviserResponded",
        label: "Adviser Responded",
        inputType: "booleanRadio",
        defaultValue: null,
        deselectable: true,
        tooltip: ADVISER_RESPONDED_TOOLTIP,
      },
    ],
  },
  {
    key: "spsCertificate",
    title: "SPS Certificate",
    fields: [
      {
        key: "spsAccreditedBody",
        label: "SPS Accredited Body",
        inputType: "select",
        options: SPS_ACCREDITED_BODY_OPTIONS,
      },
      {
        key: "spsExpiryDate",
        label: "SPS Expiry Date",
        type: "date",
      },
      {
        key: "notifyAdviser",
        label: "Notify adviser",
        inputType: "adviserSelect",
        defaultValue: "",
        tooltip:
          "Select the adviser who should receive reminder emails for this SPS certificate.",
      },
      {
        key: "adviserResponded",
        label: "Adviser Responded",
        inputType: "booleanRadio",
        defaultValue: null,
        deselectable: true,
        tooltip: ADVISER_RESPONDED_TOOLTIP,
      },
      { key: "notes", label: "Notes", inputType: "textarea" },
    ],
  },
  {
    key: "fitAndProper",
    title: "Fit & Proper",
    fields: [
      {
        key: "fitAndProperExpiryDate",
        label: "Fit & Proper Expiry Date",
        type: "date",
      },
      {
        key: "creditScore",
        label: "Credit Score",
        type: "text",
        restrictedToPermissions: CREDIT_FIELD_PERMISSIONS,
      },
      {
        key: "creditScoreDate",
        label: "Credit Score Date",
        type: "date",
        restrictedToPermissions: CREDIT_FIELD_PERMISSIONS,
      },
      {
        key: "creditReportDate",
        label: "Credit Report Date",
        type: "date",
        restrictedToPermissions: CREDIT_FIELD_PERMISSIONS,
      },
      {
        key: "ccAdvisers",
        label: "CC",
        inputType: "adviserMultiSelect",
        defaultValue: [],
      },
      {
        key: "adviserResponded",
        label: "Adviser Responded",
        inputType: "booleanRadio",
        defaultValue: null,
        deselectable: true,
        tooltip: ADVISER_RESPONDED_TOOLTIP,
      },
      { key: "notes", label: "Notes", inputType: "textarea" },
    ],
  },
  {
    key: "competentAdviser",
    title: "Competent Adviser",
    dynamic: true,
    fields: COMPETENT_ADVISER_TEST_FIELDS,
  },
  {
    key: "tc",
    title: "T&C",
    fields: [
      {
        key: "appointmentDate",
        label: "Appointment Date",
        type: "date",
      },
      {
        key: "preCASSignOff",
        label: "Pre-CAS Sign Off",
        type: "date",
      },
      {
        key: "lastOcaMeeting",
        label: "Last OCA Meeting",
        type: "date",
      },
      {
        key: "nextOcaMeeting",
        label: "Next OCA Meeting",
        type: "date",
      },
      {
        key: "ccAdvisers",
        label: "CC",
        inputType: "adviserMultiSelect",
        defaultValue: [],
      },
      {
        key: "adviserResponded",
        label: "Adviser Responded",
        inputType: "booleanRadio",
        defaultValue: null,
        deselectable: true,
        tooltip: ADVISER_RESPONDED_TOOLTIP,
      },
      { key: "notes", label: "Notes", inputType: "textarea" },
    ],
  },
];

export const getEmptyLicenseCertificates = () => {
  return LICENSE_CERTIFICATE_SECTIONS.reduce((acc, section) => {
    if (section.key === "competentAdviser") {
      acc[section.key] = {
        enabledTests: DEFAULT_COMPETENT_ADVISER_TEST_KEYS,
        ...COMPETENT_ADVISER_TEST_FIELDS.reduce((fieldAcc, field) => {
          fieldAcc[field.key] = "";
          return fieldAcc;
        }, {}),
        adviserResponded: null,
        notes: "",
      };

      return acc;
    }

    acc[section.key] = section.fields.reduce((fieldAcc, field) => {
      fieldAcc[field.key] = field.defaultValue ?? "";
      return fieldAcc;
    }, {});

    return acc;
  }, {});
};
